import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import { waitFor } from '../../minigame/match2-timestep/lib/util/waitFor';
import { runWhirlwindDancerPower } from './dreamDrift.power.dancer';
import { runPrismPainterPower } from './dreamDrift.power.painter';
import { runRumbleHeroPower } from './dreamDrift.power.rumble';
import { runSkySummonerPower } from './dreamDrift.power.summoner';

/** Asynchronous effect executed for a selected Dream Drift hero. */
export type DreamDriftHeroPower = (game: GameScene) => Promise<void>;

/** Opening power handler registered for every selectable Dream Drift hero. */
export const dreamDriftHeroPowers: Record<DreamDriftHero, DreamDriftHeroPower> =
  {
    rumbleHero: runRumbleHeroPower,
    whirlwindDancer: runWhirlwindDancerPower,
    skySummoner: runSkySummonerPower,
    prismPainter: runPrismPainterPower,
  };

/**
 * Run a hero's opening power after the initial puzzle board has settled.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param hero Hero selected for the current Dream Drift segment.
 */
export async function runDreamDriftHeroPower(
  game: GameScene,
  hero: DreamDriftHero,
): Promise<void> {
  // Wait until the initial fall and automatic shuffle phases have completed.
  if (game.phaseSystem.phase !== 'input') {
    await game.phaseSystem.waitForPhase('input');
  }

  // Leave a short beat after the settled puzzle appears before the power begins.
  await waitFor(1000);

  // Execute the selected handler and wait for its complete animation lifecycle.
  await dreamDriftHeroPowers[hero](game);
}
